<script setup>
import { computed } from 'vue';

import { useRouter } from 'vue-router';

import {
  Info,
  CheckCircle,
  TriangleAlert,
  XCircle,
  Check,
  X,
  Megaphone,
  Layers,
  Target,
  Briefcase,
  RefreshCw,
  FileText,
  FileX,
  DollarSign,
  Zap,
  Sparkles,
  Wrench,
  Bot,
  Settings,
  Archive,
} from 'lucide-vue-next';

import { formatRelativeTime, getSeverityClass } from '@/composables/useNotifications';
import { buildChangeRows } from '@/helpers/entityChangeFormat';
import { notificationTypeMapping, getNotificationTab } from '@/services/notificationSettingsService';

import NotificationEntityChange from './NotificationEntityChange.vue';

const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['mark-read', 'remove', 'click']);

// Maps the icon names declared in notificationTypeMapping to Lucide components
// (a few entries use aliases, e.g. Refresh → RefreshCw, Tool → Wrench).
const ICON_REGISTRY = {
  Megaphone,
  Layers,
  Target,
  Briefcase,
  RefreshCw,
  Refresh: RefreshCw,
  FileText,
  FileX,
  DollarSign,
  Zap,
  Sparkles,
  Info,
  CheckCircle,
  XCircle,
  AlertTriangle: TriangleAlert,
  Tool: Wrench,
};

const SEVERITY_ICONS = {
  info: Info,
  success: CheckCircle,
  warning: TriangleAlert,
  error: XCircle,
};

// Entity-update types can land under either tab (User = api_mutation,
// System = automation/sync). On the System tab pick an icon by automation
// source so the same type doesn't render an identical icon on both tabs.
const ENTITY_UPDATE_TYPES = new Set([
  'campaign_updated',
  'campaign_created',
  'adgroup_updated',
  'target_updated',
  'ad_updated',
  'portfolio_updated',
  'portfolio_created',
  'negative_target_created',
  'data_changed',
]);

const SYSTEM_SOURCE_ICONS = {
  rule_engine: Zap,
  automation: Zap,
  optimizer: Zap,
  sync: RefreshCw,
  amazon_sync: RefreshCw,
  system: Settings,
  maintenance: Wrench,
};

const router = useRouter();

const relativeTime = computed(() => formatRelativeTime(props.notification.timestamp));
const severityClass = computed(() => getSeverityClass(props.notification.severity));

// Icon shape: System entity updates → automation-source icon (so they differ
// from the User tab); otherwise by type, falling back to severity.
const iconComponent = computed(() => {
  const n = props.notification;
  if (ENTITY_UPDATE_TYPES.has(n.type) && getNotificationTab(n) === 'system') {
    return SYSTEM_SOURCE_ICONS[n.data?.source] || Bot;
  }
  // Amazon account events share one type; pick the icon by data.event
  // (archived → Archive, revoked → TriangleAlert, connected/reconnected → CheckCircle).
  if (n.type === 'amazon_account') {
    if (n.data?.event === 'archived') return Archive;
    if (n.data?.event === 'revoked') return TriangleAlert;
    return CheckCircle;
  }
  const mapped = notificationTypeMapping[n.type];
  if (mapped && ICON_REGISTRY[mapped.icon]) return ICON_REGISTRY[mapped.icon];
  return SEVERITY_ICONS[n.severity] || Info;
});

// Icon color by tab (User vs System); warning/error severities override it below.
const tabClass = computed(() => `tab-${getNotificationTab(props.notification)}`);

// Entity-update notifications carry a structured diff in data.entities. Build the
// real (changed-only) rows; if any remain, render the inline diff instead of the
// generic body. When every field was a no-op, fall back to the body text.
const changeRows = computed(() => buildChangeRows(props.notification.data));
const showDiff = computed(() => changeRows.value.length > 0);

const handleClick = () => {
  if (!props.notification.read) {
    emit('mark-read', props.notification.id);
  }

  // Navigate if URL is provided
  if (props.notification.data?.url) {
    router.push(props.notification.data.url);
    emit('click', props.notification);
  }
};

const handleRemove = (event) => {
  event.stopPropagation();
  emit('remove', props.notification.id);
};

const handleMarkRead = (event) => {
  event.stopPropagation();
  emit('mark-read', props.notification.id);
};
</script>

<template>
  <div
    class="notification-item"
    :class="[severityClass, tabClass, { unread: !notification.read, clickable: notification.data?.url }]"
    @click="handleClick"
  >
    <div class="notification-icon">
      <component :is="iconComponent" :size="14" />
    </div>

    <div class="notification-content">
      <div class="notification-header">
        <span v-no-translate class="notification-title">{{ notification.title }}</span>
        <span class="notification-time">{{ relativeTime }}</span>
      </div>

      <NotificationEntityChange v-if="showDiff" :rows="changeRows" :data="notification.data" />
      <p v-else-if="notification.body" v-no-translate class="notification-body">
        {{ notification.body }}
      </p>
    </div>

    <div class="notification-actions">
      <button
        v-if="!notification.read"
        class="action-btn mark-read-btn"
        title="Mark as read"
        @click="handleMarkRead"
      >
        <Check :size="14" />
      </button>
      <button
        class="action-btn remove-btn"
        title="Dismiss"
        @click="handleRemove"
      >
        <X :size="14" />
      </button>
    </div>

    <div v-if="!notification.read" class="unread-indicator" />
  </div>
</template>

<style lang="scss" scoped>
.notification-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 12px 8px 14px;
  background: var(--wp-surface-card);
  border-radius: 8px;
  transition: background var(--transition-fast);
  cursor: default;

  &.clickable {
    cursor: pointer;
  }

  &:hover {
    background: var(--wp-surface-hover);

    .notification-actions {
      opacity: 1;
    }
  }

  &.unread {
    background: var(--wp-surface-hover);
  }
}

.notification-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--wp-surface-hover);
}

// Base icon color by tab; warning/error severities override it (declared after).
.tab-system .notification-icon {
  color: var(--wp-blue);
}

.tab-user .notification-icon {
  color: var(--wp-state-main);
}

.notification-warning .notification-icon {
  color: var(--wp-constant-warning);
}

.notification-error .notification-icon {
  color: var(--wp-red);
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.notification-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--wp-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-time {
  font-size: 11px;
  color: var(--wp-text-tertiary);
  white-space: nowrap;
  flex-shrink: 0;
}

.notification-body {
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--wp-text-tertiary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-actions {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--wp-text-tertiary);
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--wp-surface-card);
    color: var(--wp-text-primary);
  }
}

.mark-read-btn:hover {
  color: var(--wp-constant-success);
}

.remove-btn:hover {
  color: var(--wp-red);
}

.unread-indicator {
  position: absolute;
  left: 5px;
  top: 14px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--wp-state-main);
}
</style>
